import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  DesktopDateTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import AutoCompleteList from "components/AutoCompleteList";
import { baseUrl } from "data/server_apis/server_api";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useDownloader from "react-use-downloader";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { dispatch } from "store";
import {
  getProductSoldPriceInMachineThunk,
  getProductsSoldInMachineThunk,
} from "store/reducers/statistics/statisticsSlice";
import { getMinMachinesThunk } from "store/reducers/vending";
import BottomDialog from "components/BottomDialog";
import CloseIcon from "@mui/icons-material/Close";

const ProductWiseSales = () => {
  const [machine, setMachine] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [startDate, setStartState] = React.useState(null);
  const [endDate, setEndState] = React.useState(null);

  useEffect(() => {
    dispatch(getMinMachinesThunk());
  }, []);

  useEffect(() => {
    dispatch(
      getProductsSoldInMachineThunk({
        machine: machine?.id,
        page,
        startDate,
        endDate,
      })
    );
  }, [machine, page, startDate, endDate]);

  useEffect(() => {
    dispatch(
      getProductSoldPriceInMachineThunk({
        machine: machine?.id,
        startDate,
        endDate,
      })
    );
  }, [machine, startDate, endDate]);

  const statistics = useSelector((state) => state.statistics);
  const items = statistics.productsSoldInMachine;

  const machines = useSelector((state) => state.vending.minMachines);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const token = useSelector((state) => state.auth?.user?.access);
  const [openDownload, setOpenDownload] = useState(false);

  const { elapsed, download, cancel, error, isInProgress } = useDownloader({
    mode: "cors",
    credentials: "include",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const startDownload = () => {
    setOpenDownload(true);
    const now = new Date();
    const timestamp = now.toISOString().replace(/[-:.]/g, "");
    const filename = `${timestamp}-export.csv`;
    download(
      `${baseUrl}/sales/invoice/analytics_csv_export?machine=${
        machine?.id ?? ""
      }&date_iso_after=${startDate}&date_iso_before=${endDate}`,
      filename
    );
  };

  return (
    <Stack direction="column" justifyContent="space-between" height={"100%"}>
      <Stack justifyContent="center" direction="row" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h5">
          Total value : {statistics?.productSoldPriceInMachine.toFixed(2)} Rs
        </Typography>
      </Stack>
      <Box>
        <Grid container spacing={1} sx={{ pr: 3 }}>
          <Grid item lg={4} md={6} xs={12}>
            <Stack
              alignItems="end"
              direction="row"
              spacing={2}
              marginBottom={2}
            >
              <AutoCompleteList
                items={machines}
                name="name"
                label="Filter by machine"
                setValue={(value) => {
                  setMachine(value);
                }}
                value={machine}
              />
            </Stack>
          </Grid>
          <Grid item lg={4} md={6} xs={12}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DesktopDateTimePicker
                label="From date"
                value={startDate}
                onChange={(value) => setStartState(moment(value).toISOString())}
                renderInput={(params) => (
                  <TextField fullWidth variant="outlined" {...params} />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item lg={4} md={6} xs={12}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DesktopDateTimePicker
                label="To date"
                value={endDate}
                onChange={(value) => setEndState(moment(value).toISOString())}
                renderInput={(params) => (
                  <TextField fullWidth variant="outlined" {...params} />
                )}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
        <Stack
          justifyContent="end"
          alignItems={isSmallScreen ? "start" : "end"}
        >
          <Button
            startIcon={<FileDownloadIcon />}
            variant="outlined"
            onClick={startDownload}
          >
            Export CSV
          </Button>
        </Stack>
        <Box
          sx={{ backgroundColor: "#e7ebf0", borderRadius: 2, flex: 1, mt: 2 }}
          padding={1}
        >
          <TableContainer component={Paper}>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ maxWidth: 10 }}>ID</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    {isSmallScreen ? "Name" : "Product name"}
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 120 }}>
                    Price
                  </TableCell>
                  <TableCell align="center">Qty</TableCell>
                  <TableCell align="center">Total price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell align="left" sx={{ maxWidth: 10 }}>
                      {item.id}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {item.name}
                    </TableCell>
                    <TableCell align="center">{item.price} Rs</TableCell>
                    <TableCell align="center">{item.qty} Nos</TableCell>
                    <TableCell align="center">
                      {item.price * item.qty} Rs
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
      <Stack direction="row" justifyContent="center" marginTop={2}>
        <Pagination
          count={statistics.productsSoldInMachinePageSize}
          variant="outlined"
          shape="rounded"
          onChange={(event, value) => {
            setPage(value);
          }}
        />
      </Stack>
      <BottomDialog
        open={isInProgress}
        onClose={() => {
          setOpenDownload(false);
        }}
      >
        <Stack direction="column">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h5" sx={{ ml: 2 }}>
              Downloading invoice
            </Typography>
            <IconButton
              onClick={() => {
                setOpenDownload(false);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />
          <Box sx={{ m: 2 }}>
            <LinearProgress />
          </Box>
          <Typography variant="caption" sx={{ mx: 2 }}>
            Time elapsed: {elapsed} Seconds
          </Typography>
          <Stack direction="row">
            <Box sx={{ ml: 2, mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={cancel}
              >
                Cancel download
              </Button>
            </Box>
            {error && (
              <Typography variant="subtitle1" color="red">
                Error occurred
              </Typography>
            )}
          </Stack>
        </Stack>
      </BottomDialog>
    </Stack>
  );
};

export default ProductWiseSales;
